import Chart, { ScriptableContext } from 'chart.js/auto';
import { Component, createEffect, createSignal, onCleanup } from 'solid-js';
import fonts from '../../styles/fonts.module.scss';
import { HeadwindUserSettings } from '../../types';
import { convertToUserPref, hexToRgb, objectUpdated } from '../../utils';
import { InView } from '../InView';
import styles from './lineChart.module.scss';

Chart.defaults.font.family = fonts.body;
Chart.defaults.font.size = 10;

interface Props {
  labels: string[];
  data: number[];
  color?: string;
  colors?: string[];
  activeDataPoint?: number;
  activeDataPointImage?: string;
  activeDataPointImageSize?: number;
  yAxisScaleMin?: number;
  yAxisScaleMax?: number;
  units?: keyof HeadwindUserSettings['unitPrefs'];
}

export const LineChart: Component<Props> = props => {
  const convertData = () => {
    return props.data.map(v => {
      if (props.units) {
        return convertToUserPref(v, props.units);
      }
      return v;
    });
  };

  let canvasRef: HTMLCanvasElement | undefined;
  let overlayCanvasRef: HTMLCanvasElement | undefined;
  let chart: Chart | undefined;
  const defaultColor = props.color ?? 'red';
  const dataPointImageSize = props.activeDataPointImageSize ?? 25;
  const [dataPointImage, setDataPointImage] = createSignal<HTMLImageElement | undefined>(undefined);
  let loadingPromise: Promise<HTMLImageElement | undefined> | null = null;
  let loadedImageUrl: string | undefined = undefined;
  let data = convertData();

  const syncOverlaySize = () => {
    if (overlayCanvasRef && canvasRef) {
      overlayCanvasRef.width = canvasRef.width;
      overlayCanvasRef.height = canvasRef.height;
      overlayCanvasRef.style.width = canvasRef.style.width;
      overlayCanvasRef.style.height = canvasRef.style.height;
    }
  };

  const loadActiveDataPointImage = (): Promise<HTMLImageElement | undefined> => {
    const currentUrl = props.activeDataPointImage;
    if (!currentUrl) {
      setDataPointImage(undefined);
      loadedImageUrl = undefined;
      return Promise.resolve(undefined);
    }
    if (dataPointImage() && loadedImageUrl === currentUrl) {
      return Promise.resolve(dataPointImage());
    }
    if (loadingPromise) {
      return loadingPromise;
    }

    loadingPromise = new Promise(resolve => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setDataPointImage(img);
        loadedImageUrl = currentUrl;
        loadingPromise = null;
        resolve(img);
        if (chart) {
          drawMarkerOnOverlay();
        }
      };
      img.onerror = () => {
        loadingPromise = null;
        resolve(undefined);
      };
      img.src = currentUrl;
      if (img.complete && img.naturalWidth > 0) {
        setDataPointImage(img);
        loadedImageUrl = currentUrl;
        loadingPromise = null;
        resolve(img);
      }
    });

    return loadingPromise;
  };

  const drawMarkerOnOverlay = async () => {
    if (overlayCanvasRef && chart) {
      const img = await loadActiveDataPointImage();
      const ctx = overlayCanvasRef.getContext('2d')!;
      const dpr = window.devicePixelRatio || 1;
      ctx.clearRect(0, 0, overlayCanvasRef.width, overlayCanvasRef.height);
      const index = props.activeDataPoint;
      if (index === undefined) return;
      const { scales } = chart;
      if (!scales?.x || !scales?.y) return;
      const radius = dataPointImageSize;
      let yData = data[index];
      if (yData === undefined) {
        yData = index > data.length - 1 ? data[data.length - 1] : data[0];
      }
      const x = scales.x.getPixelForValue(index) - radius / 2;
      const y = scales.y.getPixelForValue(yData) - radius / 2;
      ctx.save();
      ctx.scale(dpr, dpr);

      // Draw background circle
      ctx.beginPath();
      ctx.arc(x + radius / 2, y + radius / 2, radius / 2, 0, 2 * Math.PI);
      ctx.fillStyle = '#000';
      ctx.fill();

      // Clip and draw image if available
      const imageToDraw = img || dataPointImage();
      if (imageToDraw && imageToDraw.complete && imageToDraw.naturalWidth > 0) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(x + radius / 2, y + radius / 2, radius / 2, 0, 2 * Math.PI);
        ctx.clip();
        ctx.drawImage(imageToDraw, x, y, dataPointImageSize, dataPointImageSize);
        ctx.restore();
      }

      // Draw border stroke
      ctx.beginPath();
      ctx.strokeStyle = defaultColor;
      ctx.lineWidth = 2;
      ctx.arc(x + radius / 2, y + radius / 2, radius / 2, 0, 2 * Math.PI);
      ctx.stroke();

      ctx.restore();
    }
  };

  const clearMarkerOnOverlay = () => {
    if (overlayCanvasRef) {
      const ctx = overlayCanvasRef.getContext('2d')!;
      ctx.clearRect(0, 0, overlayCanvasRef.width, overlayCanvasRef.height);
    }
  };

  createEffect(prevActiveDataPoint => {
    if (prevActiveDataPoint !== props.activeDataPoint && chart) {
      drawMarkerOnOverlay();
    }
    return props.activeDataPoint;
  }, props.activeDataPoint);

  createEffect((prevImage?: string) => {
    if (props.activeDataPointImage !== prevImage) {
      setDataPointImage(undefined);
      loadingPromise = null;
      loadedImageUrl = undefined;
      if (props.activeDataPointImage) {
        loadActiveDataPointImage().then(() => {
          if (chart) {
            drawMarkerOnOverlay();
          }
        });
      }
    }
    return props.activeDataPointImage;
  }, props.activeDataPointImage);

  const getColorGradient = (context: ScriptableContext<'line'>, opacity: number, sampleEvery = 1) => {
    const chart = context.chart as Chart;
    const { ctx, chartArea } = chart;
    if (!chartArea || !props.colors) {
      // This case happens on initial chart creation
      return defaultColor;
    }
    const gradient = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
    data.forEach((_, i) => {
      if (i % sampleEvery === 0) {
        const percent = i / data.length;
        let color = defaultColor;
        if (props.colors && props.colors[i]) {
          color = props.colors[i];
        }
        const rgb = hexToRgb(color);
        gradient.addColorStop(percent, `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${opacity})`);
      }
    });
    return gradient;
  };

  const init = async () => {
    data = convertData();
    const backgroundSampleEvery = Math.ceil(data.length / 100);
    chart?.destroy();
    if (canvasRef && canvasRef.offsetParent) {
      chart = new Chart(canvasRef!, {
        type: 'line',
        data: {
          labels: props.labels,
          datasets: [
            {
              data,
              fill: 'start',
              backgroundColor: (context: ScriptableContext<'line'>) =>
                getColorGradient(context, 0.3, backgroundSampleEvery),
              borderColor: (context: ScriptableContext<'line'>) => getColorGradient(context, 1),
              borderWidth: 2,
              tension: 0.3,
              pointBackgroundColor: 'transparent',
              pointBorderColor: 'transparent',
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          onResize: () => {
            syncOverlaySize();
            drawMarkerOnOverlay();
          },
          animation: {
            onComplete: () => {
              if (data && data.length > 0) {
                drawMarkerOnOverlay();
              }
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
            },
          },
          scales: {
            y: {
              min: props.yAxisScaleMin,
              max: props.yAxisScaleMax,
            },
          },
        },
      });
      syncOverlaySize();
    }
  };

  const onSeen = () => {
    init();
  };

  createEffect((prevYAxisScaleMax: Props['yAxisScaleMax']) => {
    if (prevYAxisScaleMax !== props.yAxisScaleMax) {
      if (chart?.options?.scales?.y) {
        chart.options.scales.y.max = props.yAxisScaleMax;
        clearMarkerOnOverlay();
        chart.update();
      }
    }
    return props.yAxisScaleMax;
  }, props.yAxisScaleMax);

  createEffect((prevData: Props['data']) => {
    if (objectUpdated(props.data, prevData)) {
      init();
    }
    return props.data;
  }, props.data);

  onCleanup(() => {
    chart?.destroy();
  });

  return (
    <InView onSeen={onSeen}>
      <div class={styles.container}>
        <canvas ref={canvasRef} />
        <canvas class={styles.overlayCanvas} ref={overlayCanvasRef} />
      </div>
    </InView>
  );
};
